import {
  SharedFilterAutoLinks,
  SharedFilterManualLinks,
  generateColumnIdForField,
} from "@hex/common";

import { SharedFilter } from "./types.js";

export function getSharedFilterColumnNames({
  autoLinks,
  manualLinks,
}: {
  autoLinks: SharedFilterAutoLinks | null;
  manualLinks: SharedFilterManualLinks | null;
}): string[] {
  const columnNames = new Set<string>();

  autoLinks?.forEach((link) => columnNames.add(link.dataFrameColumn));
  manualLinks?.forEach((link) => columnNames.add(link.dataFrameColumn));
  return [...columnNames];
}

export function getCellSharedFilterName({
  cellDataframes,
  filter,
}: {
  filter: SharedFilter;
  cellDataframes: readonly string[];
}): string {
  const getNameInternal = (
    links: SharedFilterAutoLinks | SharedFilterManualLinks,
  ): string => {
    const link = links.find((l) => cellDataframes.includes(l.dataFrame));
    if (link) {
      return generateColumnIdForField({
        value: link.dataFrameColumn ?? "",
        queryPath: link.queryPath ?? [],
      });
    }
    return "";
  };

  if (filter.name) {
    return filter.name;
  } else if (filter.autoLinks.length === 1) {
    return getNameInternal(filter.autoLinks);
  } else if (filter.manualLinks.length === 1) {
    return getNameInternal(filter.manualLinks);
  } else if (filter.autoLinks && filter.autoLinks.length > 1) {
    return getNameInternal(filter.autoLinks);
  } else if (filter.manualLinks && filter.manualLinks.length > 1) {
    return getNameInternal(filter.manualLinks);
  }

  return "";
}

/**
 * Use this for getting a display name for shared filters at the project level.
 * If displaying shared filter for a specific cell, use getCellSharedFilterName
 * instead.
 */
export function getSharedFilterName({
  autoLinks,
  manualLinks,
  name,
}: {
  name?: string | null;
  autoLinks: SharedFilterAutoLinks | null;
  manualLinks: SharedFilterManualLinks | null;
}): string {
  const getNameInternal = (
    links: SharedFilterAutoLinks | SharedFilterManualLinks,
  ): string => {
    const numUniqColumn = new Set(links.map((link) => link.dataFrameColumn))
      .size;

    if (numUniqColumn === 1) {
      return `${links[0].dataFrameColumn}`;
    }
    return `${numUniqColumn} columns`;
  };

  if (name) {
    return name;
  } else if (autoLinks?.length === 1) {
    return autoLinks[0].dataFrameColumn;
  } else if (manualLinks?.length === 1) {
    return manualLinks[0].dataFrameColumn;
  } else if (autoLinks && autoLinks.length > 1) {
    return getNameInternal(autoLinks);
  } else if (manualLinks && manualLinks.length > 1) {
    return getNameInternal(manualLinks);
  }

  return "";
}
